import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { branch, compose, renderNothing } from 'recompose';
import { isEmpty } from 'ramda';

import { selectors as i18nSelectors, Message, translator } from 'mage-i18n';
import {
  Alert,
  defaultContainerPropTypes,
  moetPropTypes,
  FieldsetForObject,
  formUtils,
  HorizontalWrapper,
  Input,
  payloadUtils,
  Card,
} from 'moet-core';

import {
  BorderlessDropdown,
  IconError,
  Link,
  Typography,
  SpacerHorizontal,
  SpacerVertical,
} from '@klarna/bubble-ui';

import { OPTIONS_META } from '@/constants/countries';
import { validators } from '@/flow/validation';

import {
  BackButton,
  CallToActionButton,
  ContainerTemplate,
  ToggleWrappedContent,
  StakeholderFormCard,
  SwitchToggle,
} from '@/components';

import { getCountries } from '@/selectors/options';
import {
  getMerchantCountryKey,
  getMerchantAwareTranslator,
  getMerchantAwareArrayTranslator,
} from '@/selectors/merchant';
import { isUnmanagedKyc } from '@/selectors/widget';

import {
  STAKEHOLDER_ROLES_V2,
  stakeholderUtils,
  withStakeholderLogic,
} from '@/containers/withStakeholderLogic';
import { CAPS } from '@/constants';

import { getDefaultState } from '../selectors';
import { CY_SOLE_TRADER_IDS } from '../constants';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

const tPrefix = 'products-fe.containers.sole_trader';

export class SoleTraderWithOperatingAddress extends Component {
  constructor(props) {
    super(props);
    const { t, defaultState = {}, stakeholders = [], language, merchantCountry } = props;
    const { operatingAddress = {} } = defaultState;
    const { street = '', postalCode = '', city = '', country = merchantCountry } = operatingAddress;
    const state = formUtils.initFormFromState({
      language,
      toggleValue: stakeholders.length === 0 ? undefined : !isEmpty(operatingAddress),
      fields: {
        country: {
          component: BorderlessDropdown,
          size: 1,
          label: t(`${tPrefix}.address_form.fields.country.label`),
          isInitialized: false,
          isValid: false,
          validators: [
            {
              callback: validators.common.isRequired,
              errorMessage: t(`${tPrefix}.address_form.fields.country.errors.general`),
            },
          ],
          extraProps: {
            options: props.countries,
            ...generateCyElemId(CY_SOLE_TRADER_IDS.INPUT_OPERATING_ADDRESS_COUNTRY, window),
          },
          value: country,
        },
        postalCode: {
          component: Input,
          size: 1,
          type: 'text',
          label: t(`${tPrefix}.address_form.fields.postal_code.label`),
          validators: [
            {
              callback: validators.common.isRequired,
              errorMessage: t(`${tPrefix}.address_form.fields.postal_code.errors.general`),
            },
          ],
          extraProps: {
            ...generateCyElemId(CY_SOLE_TRADER_IDS.INPUT_OPERATING_ADDRESS_POSTAL_CODE, window),
          },
          isInitialized: false,
          isValid: false,
          value: postalCode,
        },
        street: {
          component: Input,
          size: 1,
          type: 'text',
          label: t(`${tPrefix}.address_form.fields.street.label`),
          validators: [
            {
              callback: validators.common.isRequired,
              errorMessage: t(`${tPrefix}.address_form.fields.street.errors.general`),
            },
          ],
          extraProps: {
            autoFocus: true,
            ...generateCyElemId(CY_SOLE_TRADER_IDS.INPUT_OPERATING_ADDRESS_STREET, window),
          },
          isInitialized: false,
          isValid: false,
          value: street,
        },
        city: {
          component: Input,
          size: 1,
          type: 'text',
          label: t(`${tPrefix}.address_form.fields.city.label`),
          validators: [
            {
              callback: validators.common.isRequired,
              errorMessage: t(`${tPrefix}.address_form.fields.city.errors.general`),
            },
          ],
          extraProps: {
            ...generateCyElemId(CY_SOLE_TRADER_IDS.INPUT_OPERATING_ADDRESS_CITY, window),
          },
          isInitialized: false,
          isValid: false,
          value: city,
        },
      },
    });

    this.state = {
      ...state,
      initialState: state,
      areYouTheSoleTrader: false,
      ctaClicked: false,
    };

    this.getBody = this.getBody.bind(this);
    this.onToggle = this.onToggle.bind(this);
    this.setAdditionalAddress = this.setAdditionalAddress.bind(this);
    this.onChangeAddressField = this.onChangeAddressField.bind(this);
    this.onBlurAddressField = this.onBlurAddressField.bind(this);
    this.onChangeAreYouTheSoleTrader = this.onChangeAreYouTheSoleTrader.bind(this);
    this.onNext = this.onNext.bind(this);
    this.isFormValid = this.isFormValid.bind(this);
  }

  static updateTranslation(key, props) {
    const { t, countries } = props;
    const fieldTranslationsMap = {
      country: {
        label: `${tPrefix}.address_form.fields.country.label`,
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: `${tPrefix}.address_form.fields.country.errors.general`,
          },
        ],
        extraProps: {
          options: countries,
        },
      },
      postalCode: {
        label: `${tPrefix}.address_form.fields.postal_code.label`,
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: `${tPrefix}.address_form.fields.postal_code.errors.general`,
          },
        ],
      },
      street: {
        label: `${tPrefix}.address_form.fields.street.label`,
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: `${tPrefix}.address_form.fields.street.errors.general`,
          },
        ],
      },
      city: {
        label: `${tPrefix}.address_form.fields.city.label`,
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: `${tPrefix}.address_form.fields.city.errors.general`,
          },
        ],
      },
    };

    return formUtils.updateTranslatedFields(fieldTranslationsMap, key, t);
  }

  static getDerivedStateFromProps(props, state) {
    const { language } = props;
    if (state.language !== language) {
      const newState = formUtils.initFormFromState({
        ...state,
        language,
        fields: {
          ...state.fields,
          ...Object.keys(state.fields).reduce((acc, field) => {
            acc[field] = {
              ...state.fields[field],
              ...SoleTraderWithOperatingAddress.updateTranslation(field, props),
            };
            return acc;
          }, {}),
        },
      });

      return {
        ...newState,
        initialState: state.initialState,
      };
    }

    return null;
  }

  onToggle(toggleValue) {
    this.setState({ toggleValue });
  }

  setAdditionalAddress(fields) {
    this.setState({ fields });
  }

  onChangeAddressField(key, val) {
    formUtils.onChangeFields(key, val, this.state, this.setAdditionalAddress);
  }

  onBlurAddressField(key) {
    formUtils.onBlurFields(key, this.state, this.setAdditionalAddress);
  }

  getBody() {
    const { fields, toggleValue } = this.state;
    const { stakeholders } = this.props;
    return {
      stakeholders: stakeholderUtils.getStakeholdersMappedValues(stakeholders),
      operatingAddress:
        toggleValue === true
          ? {
              ...formUtils.getMappedValues(fields),
            }
          : {}, // MoMa expect an empty array to force the deletion of operating address
    };
  }

  isFormValid() {
    const { roleConstraints, stakeholders } = this.props;
    const { fields, toggleValue } = this.state;

    return (
      stakeholderUtils.isStakeholderConstraintsValid(stakeholders, roleConstraints) &&
      stakeholderUtils.isStakeholdersValid(stakeholders) &&
      (toggleValue === false ||
        (toggleValue === true && formUtils.isFormInitializedAndValid(fields)))
    );
  }

  onNext() {
    const { stakeholders, roleConstraints, onNextWrapper, mid } = this.props;
    const { fields, toggleValue, areYouTheSoleTrader = false } = this.state;

    this.setState({
      ...this.state,
      fields: formUtils.initializeFields(fields),
      ctaClicked: true,
    });

    const isValid =
      stakeholderUtils.isStakeholderConstraintsValid(stakeholders, roleConstraints) &&
      stakeholderUtils.isStakeholdersValid(stakeholders) &&
      (toggleValue === false ||
        (toggleValue === true && formUtils.isFormInitializedAndValid(fields))) &&
      areYouTheSoleTrader;

    onNextWrapper(
      {
        ...payloadUtils.getMerchantCapabilityPayload(mid, this.state, this.getBody),
        hasChanged: true,
      },
      isValid,
      {
        extraFields: fields,
      },
    );
  }

  onChangeAreYouTheSoleTrader(val) {
    this.setState({ areYouTheSoleTrader: val });
  }

  render() {
    const {
      error,
      hasBack,
      isDisabled,
      isLoading,
      onBack,
      onBlurStakeholder,
      onChangeStakeholder,
      onTouchPersonalId,
      stakeholders,
      t,
      tMerchantAware,
      tArrayMerchantAware,
    } = this.props;
    const { fields, toggleValue, areYouTheSoleTrader = false, ctaClicked } = this.state;
    const stakeholder = stakeholders[0];

    const shouldShowSoleTraderError = this.isFormValid() && !areYouTheSoleTrader && ctaClicked;

    const backButton = (
      <>
        <BackButton tabIndex={16} onClick={onBack} disabled={isDisabled}>
          {t(`${tPrefix}.buttons.back`)}
        </BackButton>
        <SpacerHorizontal medium />
      </>
    );

    return (
      <ContainerTemplate
        title={t(`${tPrefix}.title`)}
        subtitle={tArrayMerchantAware(`${tPrefix}.subtitle`).map(({ text, link }, index) =>
          link ? (
            <Link key={index} href={link} target="_blank">
              {text}
            </Link>
          ) : (
            <span key={index}>{text}</span>
          ),
        )}
        info={tMerchantAware(`${tPrefix}.infobox`)}
        error={error}
      >
        <SpacerVertical small />
        <StakeholderFormCard
          autoFocus
          tabIndexStart={1}
          stakeholder={stakeholder}
          onBlur={key => onBlurStakeholder(0, key)}
          onChange={(key, value) => onChangeStakeholder(0, key, value)}
          onTouchPersonalId={() => onTouchPersonalId(0)}
          isDisabled={isDisabled}
        >
          <SpacerVertical extraExtraSmall />
          <SwitchToggle
            tabIndex={10}
            checked={areYouTheSoleTrader}
            disabled={false}
            onChange={this.onChangeAreYouTheSoleTrader}
            messagePath={`${tPrefix}.fields.are_you_the_sole_trader.label`}
            idForTestSuite={CY_SOLE_TRADER_IDS.INPUT_SOLE_TRADER_APPLICANT_SWITCH}
            error={shouldShowSoleTraderError}
          />
          {shouldShowSoleTraderError && (
            <>
              <Alert
                type="error"
                content={t(`${tPrefix}.fields.are_you_the_sole_trader.error`)}
                icon={IconError}
              />
              <SpacerVertical extraSmall />
            </>
          )}
        </StakeholderFormCard>
        <SpacerVertical large />
        <Typography textToken="text-style/headings/titles/bold/tertiary">
          {tMerchantAware(`${tPrefix}.address_form.question.title`)}
        </Typography>
        <SpacerVertical small />
        <Typography
          textToken="text-style/text/super-paragraphs/body/medium"
          colorToken="colors/text/neutral"
        >
          {tMerchantAware(`${tPrefix}.address_form.question.subtitle`)}
        </Typography>
        <SpacerVertical small />
        <ToggleWrappedContent
          error={ctaClicked && toggleValue === undefined}
          isDisabled={isDisabled}
          toggleOnChange={this.onToggle}
          toggleValue={toggleValue}
          yesContent={
            <>
              <Typography textToken="text-style/headings/titles/bold/grande">
                <Message path={`${tPrefix}.address_form.title`} />
              </Typography>
              <SpacerVertical medium />
              <Card>
                <FieldsetForObject
                  tabIndexStart={11}
                  isDisabled={isDisabled}
                  isBorderless
                  name="additional-address-uk"
                  fields={fields}
                  onBlur={this.onBlurAddressField}
                  onChange={this.onChangeAddressField}
                />
              </Card>
              <SpacerVertical large />
            </>
          }
        />
        {toggleValue !== undefined && (
          <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
            {hasBack && backButton}
            <CallToActionButton
              tabIndex={15}
              onClick={this.onNext}
              loading={isLoading}
              disabled={isDisabled}
              {...generateCyElemId(CY_SOLE_TRADER_IDS.BTN_SUBMIT, window)}
            >
              {t(`${tPrefix}.buttons.next`)}
            </CallToActionButton>
          </HorizontalWrapper>
        )}
      </ContainerTemplate>
    );
  }
}

SoleTraderWithOperatingAddress.propTypes = {
  ...defaultContainerPropTypes,
  ...moetPropTypes.defaultStakeholderContainerPropTypes,
  forceCountry: PropTypes.string.isRequired,
  isUnmanagedKyc: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  merchantCountry: PropTypes.string.isRequired,
  mid: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  tMerchantAware: PropTypes.func.isRequired,
  countries: PropTypes.array,
};

const WrappedComponent = compose(
  connect(state => ({
    t: translator(state),
    tMerchantAware: getMerchantAwareTranslator(state),
    tArrayMerchantAware: getMerchantAwareArrayTranslator(state),
    language: i18nSelectors.language(state),
    countries: getCountries(state),
    defaultState: getDefaultState(state),
    // FIXME: This true should be !isUnmanagedKyc
    forceCountry: true && getMerchantCountryKey(state),
    isUnmanagedKyc: isUnmanagedKyc(state),
    merchantCountry: getMerchantCountryKey(state),
  })),
  branch(
    ({ countries = false, currencies = false }) => !countries.length > 0 && !currencies.length > 0,
    renderNothing,
  ),
  withStakeholderLogic(CAPS.V2_MERCHANTS_STAKEHOLDERS_AND_OPERATIONAL_ADDRESS_PUT, {
    extraProps: {
      overrideStakeholders: true,
      defaultRole: STAKEHOLDER_ROLES_V2.SOLE_TRADER,
      roleConstraints: {
        [STAKEHOLDER_ROLES_V2.SOLE_TRADER]: {
          min: 1,
          max: 1,
        },
      },
    },
  }),
)(SoleTraderWithOperatingAddress);

WrappedComponent.onboardingTypes = '*';
WrappedComponent.variantId = OPTIONS_META.SOLE_TRADER_KIND.OPTIONS.WITH_OPERATING_ADDRESS;

WrappedComponent.fieldsToFill = [
  {
    fieldName: 'businessDetails',
    requiredWhenPartial: 'operatingAddress',
    fields: {
      'operatingAddress.city': { optional: true },
      'operatingAddress.country': { optional: true },
      'operatingAddress.postalCode': { optional: true },
      'operatingAddress.street': { optional: true },
    },
  },
  {
    fieldName: 'stakeholders',
    roles: [STAKEHOLDER_ROLES_V2.SOLE_TRADER],
  },
];

export default WrappedComponent;
